import Path, { basepath } from "@app/enum/Path";
import { removeAnnouncementsOffParam } from "@app/util/announcements/announcementsOffParam";
import announcementsSettings from "@app/util/announcements/announcementsSettings";
import { getWebOrigin, isAllowedHandoffReturn } from "@app/util/appUrl";
import loggingService from "@app/util/logging";

import configService from "../config/ConfigService";
import localSettings from "../config/LocalSettings";

// This file contains code needed to be executed when app starts
// It's moved from the index.tsx file to shorten it
import authService from "../store/auth/AuthService";

/**
 * Extracts token from the URL if it is present there, saves it, and removes query
 * from the URL. A one-time handoff `code` takes precedence over the legacy token
 * params, since a URL only ever carries one or the other.
 */
// Drops the consumed auth params and rewrites the URL, keeping any others + the hash.
const stripAuthParamsFromUrl = (params: URLSearchParams) => {
  params.delete("mmc_handoff_code");
  params.delete("mmc_return");
  params.delete("mmc_token");
  const query = params.toString();
  window.history.replaceState(
    {},
    document.title,
    `${window.location.origin}${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
  );
};

export const readTokenFromUrlIfPresent = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  // namespaced so it can't collide with the `code` OAuth callbacks and password-reset
  // links carry — those must reach the page that expects them
  if (urlParams.has("mmc_handoff_code")) {
    const code = urlParams.get("mmc_handoff_code") as string;
    try {
      // Bound the exchange: a hung backend must not block app startup indefinitely — on
      // timeout (or any error) we fall through to the normal login flow below.
      const response = await fetch(
        `${configService.getBaseApiUrl()}/token-handoff/code/${encodeURIComponent(code)}`,
        { signal: AbortSignal.timeout(5000) }
      );
      if (response.ok) {
        const { skeletonKeyUsed, token } = (await response.json()) as {
          skeletonKeyUsed?: boolean;
          token?: string;
        };
        if (token) {
          authService.setToken(token);
          // Recorded as a sign-in here would, so start-up skips writes a support session can't make.
          localSettings.setSkeletonKeyUsed(skeletonKeyUsed === true);
          localSettings.setDefaultMapFilterPending(skeletonKeyUsed !== true);
        }
      } else {
        // a rejected code (expired/consumed/invalid) is expected — warn rather than error,
        // so it's distinguishable from a backend outage in Sentry; user still lands on login
        loggingService.warning("token-handoff code exchange returned a non-ok response", {
          status: response.status,
        });
      }
    } catch (error) {
      // exchange failed, timed out, or is unreachable — leave the user to the normal login flow
      loggingService.error("token-handoff code exchange failed", { error });
    }
    stripAuthParamsFromUrl(urlParams);
  } else if (urlParams.has("mmc_token")) {
    const token = urlParams.get("mmc_token") as string;
    if (token !== authService.getToken()) {
      authService.setToken(token);
    }
    stripAuthParamsFromUrl(urlParams);
  }
};

/**
 * Logouts and redirects to the login page if mmc_logout param is present.
 */
export const logoutIfAskedFor = () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("mmc_logout")) {
    authService.resetSession();
    announcementsSettings.setAnnouncementsOffInTab(false);
    window.history.replaceState({}, document.title, configService.getLogoutUrl());
  }
};

/**
 * Remembers mmc_redirect parameter to redirect to after logging in.
 */
export const rememberRedirectLink = () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("mmc_redirect")) {
    const redirect = urlParams.get("mmc_redirect")!;
    localSettings.setLocation(redirect);
  }
};

// Pages a sign-in passes through. A full load of any other page ends a waiting hand-over, so a
// user who leaves it and signs in later isn't sent to the DE.
const HANDOFF_SIGN_IN_PATHS: readonly string[] = [
  Path.ROOT,
  Path.AUTH_CALLBACK,
  Path.FORGOT_PASSWORD,
  Path.LOGIN,
  Path.SSO_LOGIN,
];

// Remembers a DE page that asked to borrow this app's sign-in, if its origin is allowed.
export const rememberHandoffReturn = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const returnUrl = urlParams.get("mmc_return");
  if (returnUrl === null) {
    const path = window.location.pathname.slice(basepath.length).toLowerCase();
    if (!HANDOFF_SIGN_IN_PATHS.includes(path)) {
      localSettings.resetHandoffReturn();
    }
    return;
  }
  if (isAllowedHandoffReturn(returnUrl, configService.getHandoffOrigins())) {
    localSettings.setHandoffReturn(returnUrl);
  } else {
    loggingService.warning("sign-in handoff refused for an origin that is not allowed", {
      origin: getWebOrigin(returnUrl),
    });
  }
  stripAuthParamsFromUrl(urlParams);
};

/**
 * Remembers if app was opened from the mobile app.
 */
export const checkIfOpenedFromMobileApp = () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("mmc_mobile")) {
    localSettings.setOpenedFromMobileApp(true);
  }
};

/**
 * Turns announcements off for this tab when an announcement CTA opened it, and cleans the URL.
 */
export const checkIfOpenedFromAnnouncementCta = () => {
  const cleanUrl = removeAnnouncementsOffParam(window.location);
  if (cleanUrl === undefined) {
    return;
  }
  announcementsSettings.setAnnouncementsOffInTab(true);
  window.history.replaceState(window.history.state, document.title, cleanUrl);
};

/**
 * Reloads this tab when the auth token is changed by another tab. localStorage
 * is shared across tabs of the same origin, so a logout/login in one tab can
 * leave another tab using a stale token (or no token at all). Reloading lets
 * this tab re-sync with whatever localStorage now says.
 */
export const watchCrossTabAuthChanges = () => {
  let lastKnownToken = localSettings.getAuthToken();
  window.addEventListener("storage", () => {
    const currentToken = localSettings.getAuthToken();
    if (currentToken !== lastKnownToken) {
      lastKnownToken = currentToken;
      // A sign-out in another tab also ends this tab's announcement-free session.
      if (!currentToken) {
        announcementsSettings.setAnnouncementsOffInTab(false);
        // The reload loses this tab's user, so the page is saved with it first.
        const { hash, pathname, search } = window.location;
        try {
          // The router store holds the decoded pathname, and a save with no owner keeps the owner
          // only for an identical string.
          authService.saveLocation(
            `${decodeURI(pathname.slice(basepath.length))}${search}${hash}`,
            localSettings.getUserId()
          );
        } catch {
          // A path the router cannot decode has nothing to keep.
        }
      }
      window.location.reload();
    }
  });
};
